import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, MessageSquare, User, Building2, ArrowLeft } from 'lucide-react';
import api from '../../services/api';

const ratingColors = {
  5: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  4: 'bg-blue-100 text-blue-700 border-blue-200',
  3: 'bg-amber-100 text-amber-700 border-amber-200',
  2: 'bg-orange-100 text-orange-700 border-orange-200',
  1: 'bg-red-100 text-red-700 border-red-200',
};

export default function SatisfactionPage() {
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    Promise.all([
      api.get('/satisfaction'),
      api.get('/satisfaction/stats')
    ]).then(([surveyRes, statsRes]) => {
      setSurveys(surveyRes.data);
      setStats(statsRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? surveys : surveys.filter(s => s.Rating === parseInt(filter));

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/staff/dashboard" className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft size={18} /></Link>
          <h1 className="text-2xl font-bold text-slate-800">Müşteri Değerlendirmeleri</h1>
        </div>
      </div>

      {/* Stats */}
      {stats && stats.TotalSurveys > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 text-center">
            <p className="text-3xl font-bold text-slate-800">{(stats.AvgRating || 0).toFixed(1)}</p>
            <div className="flex justify-center gap-0.5 mt-1">
              {[1,2,3,4,5].map(s => (
                <span key={s} className={`text-sm ${s <= Math.round(stats.AvgRating || 0) ? 'text-amber-400' : 'text-slate-200'}`}>★</span>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1">Ortalama</p>
          </div>
          {[5,4,3,2,1].map(r => (
            <div key={r} className={`rounded-xl shadow-sm border p-4 text-center cursor-pointer transition-all ${filter === String(r) ? 'ring-2 ring-blue-400' : ''} ${ratingColors[r]}`}
              onClick={() => setFilter(filter === String(r) ? 'all' : String(r))}>
              <p className="text-2xl font-bold">{stats[`Rating${r}`] || 0}</p>
              <p className="text-xs font-medium mt-1">{r} ★</p>
            </div>
          ))}
        </div>
      )}

      {/* Survey List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-800">Değerlendirmeler ({filtered.length})</h2>
          {filter !== 'all' && (
            <button onClick={() => setFilter('all')} className="text-xs text-blue-600 hover:underline">Tümünü Göster</button>
          )}
        </div>
        <div className="divide-y divide-slate-100">
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10">Henüz değerlendirme yok</p>
          ) : filtered.map(s => (
            <div key={s.Id} className="px-5 py-4 hover:bg-slate-50 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(r => (
                        <span key={r} className={`text-sm ${r <= s.Rating ? 'text-amber-400' : 'text-slate-200'}`}>★</span>
                      ))}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${ratingColors[s.Rating]}`}>
                      {s.Rating}/5
                    </span>
                  </div>
                  <Link to={`/staff/tickets/${s.TicketId}`} className="text-sm font-medium text-blue-600 hover:underline">
                    #{s.TicketId} - {s.TicketTitle}
                  </Link>
                  {s.Comment && (
                    <div className="mt-2 flex items-start gap-2">
                      <MessageSquare size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-600">{s.Comment}</p>
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0 space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 justify-end">
                    <User size={12} />
                    <span>{s.ContactName || s.ContactEmail}</span>
                  </div>
                  {s.CustomerName && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-400 justify-end">
                      <Building2 size={12} />
                      <span>{s.CustomerName}</span>
                    </div>
                  )}
                  {s.AssignedToName && (
                    <p className="text-[10px] text-slate-400">Atanan: {s.AssignedToName}</p>
                  )}
                  <p className="text-[10px] text-slate-400">{new Date(s.CreatedAt).toLocaleDateString('tr-TR')}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
